import { createColumnHelper } from "@tanstack/react-table";
import { getFormattedDate } from "src/features/fields/utils/utils";
import { CheckCircle2 } from "lucide-react";

const columnHelper = createColumnHelper();

// Rounds to a whole rupee and adds thousands separators, e.g. 30923.3299999999 -> "30,923".
const formatRoundedCurrencyValue = (value: number) => Math.round(value).toLocaleString("en-IN");

// Used for both `cell` and `aggregatedCell`: tanstack's grouping feature defaults
// `aggregatedCell` to a plain `.toString()` of the aggregated value, which bypasses
// this formatting for grouped/tree-view rows unless set explicitly here.
const currencyCell = (info: any) => {
  const value = info.getValue();
  return value ? `Rs. ${formatRoundedCurrencyValue(Number(value))}` : null;
};

export const ExpectedSettlementColumns = [
  columnHelper.accessor("OrderType_Name", {
    cell: (info) => info.getValue(),
    header: () => "Type",
    enableSorting: true,
  }),
  columnHelper.accessor("Order_Number", {
    cell: (info) => info.getValue(),
    header: () => "Order No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Date", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "Stk Inv. Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Number", {
    cell: (info) => info.getValue(),
    header: () => "Stk Inv. No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Company_Name", {
    cell: (info) => info.getValue(),
    header: () => "Company",
    enableSorting: true,
  }),
  columnHelper.accessor("Division_Name", {
    cell: (info) => info.getValue(),
    header: () => "Division",
    enableSorting: true,
  }),
  columnHelper.accessor("Item_Name", {
    cell: (info) => info.getValue(),
    header: () => "Product",
    enableSorting: true,
  }),
  columnHelper.accessor("Expected_Date_of_Release", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "Expected Release Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("CN_Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "CN Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("Quantity", {
    cell: (info) => info.getValue(),
    header: () => "Quantity",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Type", {
    cell: (info) => info.getValue(),
    header: () => "Discount Type",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Value", {
    cell: (info: any) => {
      const discountValue = info.getValue();
      const discountType = info.row.original?.Discount_Type;
      return discountType === "Scheme" ? discountValue : discountType === "Discount" ? `${discountValue}%` : `₹ ${discountValue}`;
    },
    header: () => "Discount Value",
    enableSorting: true,
  }),
];

export const ReleasedSettlementColumns = [
  columnHelper.accessor("OrderType_Name", {
    cell: (info) => info.getValue(),
    header: () => "Type",
    enableSorting: true,
  }),
  columnHelper.accessor("PO_Number", {
    cell: (info) => info.getValue(),
    header: () => "PO Number",
    enableSorting: true,
  }),
  columnHelper.accessor("PO_Date", {
    cell: (info) => {
      const value = info.getValue();
      console.log("value: po date", value)
      return value ? getFormattedDate(value) : null;
    },
    header: () => "PO Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Order_Number", {
    cell: (info) => info.getValue(),
    header: () => "Order No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Date", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "Stk Inv. Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Number", {
    cell: (info) => info.getValue(),
    header: () => "Stk Inv. No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Company_Name", {
    cell: (info) => info.getValue(),
    header: () => "Company",
    enableSorting: true,
  }),
  columnHelper.accessor("Division_Name", {
    cell: (info) => info.getValue(),
    header: () => "Division",
    enableSorting: true,
  }),
  columnHelper.accessor("Item_Name", {
    cell: (info) => info.getValue(),
    header: () => "Product",
    enableSorting: true,
  }),
  columnHelper.accessor("Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("CN_Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "CN Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("CN_Flg", {
    cell: (info) => {
      const value = info.getValue();

      if (typeof value !== "boolean") return null;

      return (
        <CheckCircle2
          size={18}
          className={value ? "text-green-600" : "text-gray-300"}
        />
      );
    },
    header: () => "CN",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Type", {
    cell: (info) => info.getValue(),
    header: () => "Discount Type",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Value", {
    cell: (info: any) => {
      const discountValue = info.getValue();
      const discountType = info.row.original?.Discount_Type;
      return discountType === "Scheme" ? discountValue : discountType === "Discount" ? `${discountValue}%` : `₹ ${discountValue}`;
    },
    header: () => "Discount Value",
    enableSorting: true,
  }),
  columnHelper.accessor("Quantity", {
    cell: (info) => info.getValue(),
    header: () => "Quantity",
    enableSorting: true,
  }),
];

export const SettledSettlementColumns = [
  columnHelper.accessor("OrderType_Name", {
    cell: (info) => info.getValue(),
    header: () => "Type",
    enableSorting: true,
  }),
  columnHelper.accessor("PO_Number", {
    cell: (info) => info.getValue(),
    header: () => "PO Number",
    enableSorting: true,
  }),
  columnHelper.accessor("PO_Date", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "PO Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Order_Number", {
    cell: (info) => info.getValue(),
    header: () => "Order No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Date", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "Stk Inv. Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Stk_Invoice_Number", {
    cell: (info) => info.getValue(),
    header: () => "Stk Inv. No.",
    enableSorting: true,
  }),
  columnHelper.accessor("Company_Name", {
    cell: (info) => info.getValue(),
    header: () => "Company",
    enableSorting: true,
  }),
  columnHelper.accessor("Division_Name", {
    cell: (info) => info.getValue(),
    header: () => "Division",
    enableSorting: true,
  }),
  columnHelper.accessor("Item_Name", {
    cell: (info) => info.getValue(),
    header: () => "Product",
    enableSorting: true,
  }),
  columnHelper.accessor("Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("CN_Amount", {
    cell: currencyCell,
    aggregatedCell: currencyCell,
    header: () => "CN Amount",
    enableSorting: true,
  }),
  columnHelper.accessor("CN_Flg", {
    cell: (info) => {
      const value = info.getValue();

      if (typeof value !== "boolean") return null;

      return (
        <CheckCircle2
          size={18}
          className={value ? "text-green-600" : "text-gray-300"}
        />
      );
    },
    header: () => "CN",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Type", {
    cell: (info) => info.getValue(),
    header: () => "Discount Type",
    enableSorting: true,
  }),
  columnHelper.accessor("Discount_Value", {
    cell: (info: any) => {
      const discountValue = info.getValue();
      const discountType = info.row.original?.Discount_Type;
      return discountType === "Scheme" ? discountValue : discountType === "Discount" ? `${discountValue}%` : `₹ ${discountValue}`;
    },
    header: () => "Discount Value",
    enableSorting: true,
  }),
  columnHelper.accessor("Quantity", {
    cell: (info) => info.getValue(),
    header: () => "Quantity",
    enableSorting: true,
  }),
  columnHelper.accessor("Company_Billed_Date", {
    cell: (info) => {
      const value = info.getValue();
      return value ? getFormattedDate(value) : null;
    },
    header: () => "Company Bill Date",
    enableSorting: true,
  }),
  columnHelper.accessor("Company_Billed_Number", {
    cell: (info) => info.getValue(),
    header: () => "Company Bill No.",
    enableSorting: true,
  }),
];
